import {User} from "../../../../../pages/shared/models/user/user";
import {
    BarekamColumns,
    OrdersTableColumn,
    WarehouseWorkerColumns
} from "../../../../../pages/orders/my/models/column-models";
import React, {useEffect, useState} from "react";
import {OrdersResponseModel} from "../../../../../pages/shared/models/api/orders-response.model";
import ordersService from "../../../../../services/orders.service";
import {TableBody, TableHead, TableRow} from "@mui/material";
import {
    OrderListContainer,
    OrderTable,
    OrderTableCell,
    OrderTableRow
} from "../../../../../pages/orders/my/order-list/styled";
import BarekamText from "../../../../../pages/components/text";
import {Message, MessageSections} from "../../../../../pages/components/message/message";
import {ToolPanel} from "../../../../../pages/orders/my/tool-panel/tool-panel";
import {RowCell} from "../../../../../pages/orders/my/order-list/cell/row-cell";
import BarekamPagination from "../../../../../pages/orders/my/pagination/barekam-pagination";
import {useAppSelector} from "../../../../../redux/store";
import {OrderModel} from "../../../../../pages/shared/models/order/order-item.model";
import {useNavigate} from "react-router-dom";
import NoOrders from "../../../../../pages/orders/my/no-orders";
import {GenericActionTypes} from "../../../../actions/action-types.enum";
import {ActionDialog} from "../../../../actions/dialogs/action-dialog";

const internalTrackingNumberColumn: OrdersTableColumn = {field: 'internalTrackingNumber', headerName: 'order_list_barekam_tracking', variant: BarekamColumns.barekamTrack};
const productTrackNumbersColumn: OrdersTableColumn = {field: 'products', headerName: 'order_list_tracking', variant: BarekamColumns.productTrackNumbers};
const nameColumn: OrdersTableColumn = {field: 'name', headerName: 'order_list_name', variant: BarekamColumns.text};
const shippingInfoColumn: OrdersTableColumn = {field: 'shippingInfo', headerName: 'order_list_sender', variant: BarekamColumns.sender,};
const shippingCategoryColumn: OrdersTableColumn = {field: 'shippingInfo', headerName: 'order_list_shipping_category', variant: BarekamColumns.shippingCategory,};
const recipientColumn: OrdersTableColumn = {field: 'recipient', headerName: 'order_list_recipient', variant: BarekamColumns.recipient,};
const paymentInfoColumn: OrdersTableColumn = {field: 'paymentInfo', headerName: 'order_list_price', variant: BarekamColumns.money,};
const appliedDiscountColumn: OrdersTableColumn = {field: 'paymentInfo', headerName: 'order_list_discount', variant: BarekamColumns.discount,};
const paymentStatusColumn: OrdersTableColumn = {field: 'paymentStatus', headerName: 'order_list_payment', variant: BarekamColumns.paymentStatus,};
const additionalServicesColumn: OrdersTableColumn = {field: 'additionalService', headerName: 'order_list_additional_services', variant: BarekamColumns.additionalServices,};
const allOrdersActionsColumn: OrdersTableColumn = {field: '', headerName: 'order_list_actions', variant: WarehouseWorkerColumns.allOrdersActions,}

const columns = [
    nameColumn,
    internalTrackingNumberColumn,
    productTrackNumbersColumn,
    recipientColumn,
    shippingInfoColumn,
    shippingCategoryColumn,
    paymentInfoColumn,
    appliedDiscountColumn,
    paymentStatusColumn,
    additionalServicesColumn,
    allOrdersActionsColumn
]

const CustomerOrders = ({customer}: {customer?: User}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [ordersData, setOrdersData] = useState({
        currentPage: 0,
        orders: [],
        pageSize: 10,
        totalItems: 0,
    } as OrdersResponseModel);

    const [activeAction, setActiveAction] = useState<string>();
    const [activeRowOrders, setActiveRowOrders] = useState([] as OrderModel[]);

    const {filter} = useAppSelector(state => state.ordersReducer);
    const {orderStateId} = useAppSelector(state => state.ordersReducer);

    const navigate = useNavigate();

    useEffect(() => {
        if (customer) {
            loadOrders(ordersData.currentPage);
        }
    }, [filter, customer])

    function loadOrders(page: number) {
        ordersService.getAllOrders({relatedUserId: customer?.userId, ...filter}, ordersData.pageSize, page)
            .then(response => {
                setOrdersData(response);
                setIsLoading(false);
            })
    }

    function handlePageChange (event: unknown, newPage: number) {
        loadOrders(newPage - 1);
    }

    function openAction (order: OrderModel) {
        navigate(`../order/${order.orderId}`);
    }

    function onRowAction(row: OrderModel, action: any) {
        switch (action) {
            case GenericActionTypes.SEE_STATUS_HISTORY:
                setActiveRowOrders([row]);
                setActiveAction(action);
                break;
            case GenericActionTypes.SET_DISCOUNT:
                setActiveRowOrders([row]);
                setActiveAction(action);
                break;
        }
    }

    function handleActionDialogClosed() {
        if (activeAction === GenericActionTypes.SET_DISCOUNT) {
            handlePageChange('', ordersData.currentPage + 1);
        }

        setActiveAction(undefined);
        setActiveRowOrders([]);
    }

    return (
        <>
            <ToolPanel selectedOrders={new Map()}
                       isWarehouseWorker={true}
                       orderStateId={orderStateId}
                       inCustomerOrdersPage/>

            <OrderListContainer>
                {ordersData.orders.length
                    ? <OrderTable width={'100%'}>
                        <OrderListTableHead/>

                        <TableBody>
                            {ordersData.orders.map((order, index) => {
                                return (
                                    <OrderTableRow
                                        hover
                                        tabIndex={index}
                                        key={`${index}-${order.orderId}`}>
                                        {columns.map((column, columnIndex) => {
                                            return (
                                                <RowCell cellVariant={column.variant}
                                                         field={column.field}
                                                         row={order}
                                                         groups={[]}
                                                         isName={columnIndex === 0}
                                                         key={`${columnIndex}-${order.orderId}-${column.field}`}
                                                         clicked={openAction}
                                                         onAction={onRowAction}
                                                         payButtonClicked={() => void(0)}
                                                         isWarehouseWorker={true}
                                                />
                                            )
                                        })}
                                    </OrderTableRow>
                                );
                            })}
                        </TableBody>
                    </OrderTable>
                    : isLoading ? '' : <NoOrders/>
                }

                {ordersData.totalItems > ordersData.pageSize
                    ? <BarekamPagination {...ordersData} onPageChange={handlePageChange}/>
                    : <></>
                }
            </OrderListContainer>

            <ActionDialog open={!!activeAction}
                          closePopup={() => handleActionDialogClosed()}
                          action={activeAction}
                          predefinedOrders={activeRowOrders}
                          historyDialogProps={activeRowOrders[0]?.orderId ? {category: 'Order', categoryId: activeRowOrders[0].orderId} : undefined}/>
        </>
    )
};

const OrderListTableHead = () => {
    return (
        <TableHead>
            <TableRow>
                {columns.map((column, index) => (
                    <OrderTableCell key={`${index}-${column.field}`}>
                        <BarekamText variant={'m'} align={index === columns.length - 1 ? 'right' : 'left'}>
                            <Message messageKey={column.headerName} sectionId={MessageSections.ORDERS}/>
                        </BarekamText>
                    </OrderTableCell>
                ))}
            </TableRow>
        </TableHead>
    )
}

export default CustomerOrders;
